import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { type Control, Controller, type FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type TRules } from "@/shared/types/form.types";
import MoneyInput from "./MoneyInput/MoneyInput";
import { TextInputStyled } from "./TextInput.style";
import { TbEye, TbEyeOff } from "react-icons/tb";
import { inputStyles, type InputVariant } from "./inputStyles";

const initialValidation = (
  type: React.InputHTMLAttributes<unknown>["type"],
) => ({
  required: { value: true, message: "GENERAL.REQUIRED" },
  minLength:
    type === "password"
      ? {
          value: 6,
          message: "general.minLength6",
        }
        : undefined,
});

export interface ITextField<FormNames extends Record<string, any>> {
  control: Control<FormNames>;
  name: FieldPath<FormNames>;
  rules?: TRules<FormNames>;
  label?: string;
  placeholder?: string | number;
  type?: React.InputHTMLAttributes<unknown>["type"];
  multiline?: boolean;
  disabled?: boolean;
  onCustomChange?: (val: string) => void;
  inputProps?: any;
  returnValueAsString?: boolean;
  maxRows?: number;
  rows?: number;
  className?: string;
  onClick?: (val: string) => void;
  inputRef?: React.Ref<any>; // <-- added optional inputRef
  variant?: InputVariant;
}

function TextInput<FormNames extends Record<string, any>>({
  label,
  control,
  name,
  placeholder = "",
  type = "text",
  multiline = false,
  rules,
  disabled = false,
  inputProps,
  returnValueAsString = false,
  onCustomChange,
  maxRows,
  rows,
  className,
  onClick,
  inputRef, // <-- added here
  variant = "default",
}: ITextField<FormNames>) {
  const [showPassword, setShowPassword] = useState(true);
  const { t } = useTranslation();

  if (type === "number") {
    return (
      <MoneyInput
        control={control}
        name={name}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        inputProps={inputProps}
        onCustomChange={onCustomChange}
        onClick={(e: any) => onClick?.(e.target.value)}
        returnValueAsString={returnValueAsString}
        variant={variant}
        rules={{
          ...initialValidation("number"),
          ...rules,
        }}
      />
    );
  }
  const textInputRules = {
    ...initialValidation(type),
    ...rules,
  };
  return (
    <Controller
      name={name}
      control={control}
      rules={textInputRules}
      // defaultValue={type === "text" ? "" : undefined}
      render={({
        field: { onChange, onBlur, value, name, ref },
        fieldState: { error },
      }) => {
        return (
          <TextInputStyled disabled={disabled}>
            {label && (
              <label htmlFor={name}>
                {label}
                <span>
                {label && textInputRules?.required ? (
                  <span className="!text-red-500">*</span>
                ) : null}
                </span>
              </label>
            )}
            <TextField
              className={className}
              size="small"
              type={
                type === "password"
                  ? showPassword
                    ? "password"
                    : "text"
                  : type
              }
              onChange={(e) => {
                onChange(e);
                onCustomChange?.(e.target?.value);
              }}
              onBlur={onBlur}
              onClick={(e: any) => onClick?.(e.target.value)}
              disabled={disabled}
              autoComplete="off"
              value={value ?? ""}
              name={name}
              placeholder={String(placeholder)}
              ref={inputRef || ref} // <-- use inputRef if provided, else use react-hook-form ref
              id={name}
              error={!!error}
              sx={inputStyles[variant]}
              slotProps={{
                input: {
                  ...inputProps,
                  endAdornment: type === "password" && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <TbEye /> : <TbEyeOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              variant="outlined"
              multiline={multiline}
              rows={rows}
              maxRows={maxRows}
            />
            {error && (
              <h6 className="text-error">
                {t(error.message! || "GENERAL.REQUIRED")}
              </h6>
            )}
          </TextInputStyled>
        );
      }}
    />
  );
}

export default TextInput;
